"use client";

import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useDebouncedValue } from "@tanstack/react-pacer";
import { MoreHorizontal, Pencil, Pin, PinOff, Search, Trash2 } from "lucide-react";
import { AccessDeniedCard } from "@/components/shared/access-denied-card";
import { DeleteConfirmationDialog } from "@/components/shared/delete-dialog";
import { PermissionGate } from "@/components/shared/permission-gate";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useCommandSessions } from "@/hooks/use-command-sessions";
import { formatSessionSidebarTime } from "@/utils/command-session";
import { SEARCH_DEBOUNCE_WAIT } from "@/utils/global";
import { cn } from "@/lib/utils";
import { useCommandPanel } from "./command-panel-provider";
import type { CommandSession } from "@/types/command-agent";

interface CommandPanelHistoryProps {
    onSelectSession: (sessionId: string) => void;
}

interface SessionListSkeletonProps {
    count?: number;
}

function SessionListSkeleton({ count = 6 }: SessionListSkeletonProps) {
    return (
        <div className="space-y-0.5">
            {Array.from({ length: count }).map((_, index) => (
                <div key={index} className="rounded-lg px-2.5 py-2">
                    <div className="space-y-2">
                        <Skeleton className="h-4 w-[75%]" />
                        <Skeleton className="h-3 w-[45%]" />
                    </div>
                </div>
            ))}
        </div>
    );
}

export function CommandPanelHistory({ onSelectSession }: CommandPanelHistoryProps) {
    const { thread, activeAgent } = useCommandPanel();
    const { currentSessionId, startNewChat } = thread;

    const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
    const [editTitle, setEditTitle] = useState("");
    const [originalTitle, setOriginalTitle] = useState("");
    const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch] = useDebouncedValue(searchQuery, { wait: SEARCH_DEBOUNCE_WAIT });
    const inputRef = useRef<HTMLInputElement>(null);
    const trimmedSearch = searchQuery.trim();
    const debouncedTrimmedSearch = debouncedSearch.trim();

    const {
        allSessions,
        isLoadingSessions,
        isFetchingSessions,
        canListSessions,
        hasNextPage: hasNextSessionsPage,
        isFetchingNextPage: isFetchingNextSessionsPage,
        loadMoreRef: sessionsLoadMoreRef,
        updateSession,
        isUpdatingSession,
        deleteSession,
        isDeletingSession,
    } = useCommandSessions(activeAgent?.id ?? null, {
        search: debouncedTrimmedSearch || undefined,
    });

    const showListSkeleton =
        (isLoadingSessions || isFetchingSessions) && allSessions.length === 0;

    useEffect(() => {
        if (editingSessionId) inputRef.current?.focus();
    }, [editingSessionId]);

    if (!canListSessions) {
        return (
            <div className="p-4">
                <AccessDeniedCard message="You don't have permission to view past Command sessions." />
            </div>
        );
    }

    const startEdit = (session: CommandSession) => {
        setEditingSessionId(session.id);
        setEditTitle(session.title || "");
        setOriginalTitle(session.title || "");
    };

    const cancelEdit = () => {
        setEditingSessionId(null);
        setEditTitle("");
        setOriginalTitle("");
    };

    const saveEdit = (sessionId: string) => {
        const trimmed = editTitle.trim();
        if (!trimmed || trimmed === originalTitle) {
            cancelEdit();
            return;
        }
        updateSession({ sessionId, title: trimmed });
        cancelEdit();
    };

    const handleTitleKeyDown = (e: KeyboardEvent<HTMLInputElement>, sessionId: string) => {
        if (e.key === "Enter") {
            e.preventDefault();
            saveEdit(sessionId);
        } else if (e.key === "Escape") {
            e.preventDefault();
            cancelEdit();
        }
    };

    const handleDeleteConfirm = () => {
        if (!deleteTargetId) return;
        const idToDelete = deleteTargetId;
        deleteSession(idToDelete, {
            onSuccess: () => {
                if (currentSessionId === idToDelete) startNewChat();
                setDeleteTargetId(null);
            },
        });
    };

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div className="shrink-0 border-b px-3 py-2.5">
                <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search sessions..."
                    className="h-9"
                    startIcon={<Search className="size-4 text-muted-foreground" />}
                />
            </div>

            <div className="app-scrollbar min-h-0 flex-1 overflow-y-auto px-3 py-3">
                {showListSkeleton ? (
                    <SessionListSkeleton />
                ) : allSessions.length === 0 ? (
                    <p className="px-2 py-8 text-center text-sm text-muted-foreground">
                        {trimmedSearch
                            ? `No sessions match “${trimmedSearch}”.`
                            : "No chats yet with this agent."}
                    </p>
                ) : (
                    <div className="space-y-0.5">
                        {allSessions.map((session) => {
                            const isActive = session.id === currentSessionId;
                            const isEditing = editingSessionId === session.id;

                            return (
                                <div
                                    key={session.id}
                                    onClick={() => {
                                        if (isEditing) return;
                                        onSelectSession(session.id);
                                    }}
                                    className={cn(
                                        "group relative cursor-pointer rounded-lg px-2.5 py-2 transition-colors",
                                        isActive
                                            ? "bg-violet-100 font-medium text-violet-700 dark:bg-violet-900/30 dark:text-violet-300"
                                            : "hover:bg-muted/60"
                                    )}
                                >
                                    {isEditing ? (
                                        <input
                                            ref={inputRef}
                                            type="text"
                                            value={editTitle}
                                            onChange={(e) => setEditTitle(e.target.value)}
                                            onBlur={() => saveEdit(session.id)}
                                            onKeyDown={(e) => handleTitleKeyDown(e, session.id)}
                                            onClick={(e) => e.stopPropagation()}
                                            className="w-full rounded-md border border-border bg-transparent px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-primary"
                                        />
                                    ) : (
                                        <div className="flex items-start justify-between gap-1">
                                            <div className="min-w-0 flex-1 space-y-0.5">
                                                <p className="truncate text-sm leading-snug">
                                                    {session.title || "Untitled chat"}
                                                </p>
                                                <p className="truncate text-[11px] text-muted-foreground">
                                                    {activeAgent ? `${activeAgent.name} · ` : ""}
                                                    {formatSessionSidebarTime(session.updated_at ?? session.created_at)}
                                                </p>
                                            </div>
                                            <div className="flex shrink-0 items-center gap-1 pt-0.5">
                                                {session.pinned ? (
                                                    <Pin className="size-3 text-muted-foreground/60" />
                                                ) : null}
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <span
                                                            role="button"
                                                            tabIndex={0}
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="flex size-5 items-center justify-center rounded-md opacity-0 transition-opacity hover:bg-muted-foreground/20 group-hover:opacity-100 data-[state=open]:bg-muted-foreground/20 data-[state=open]:opacity-100"
                                                        >
                                                            <MoreHorizontal className="size-3.5" />
                                                        </span>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="w-[150px]">
                                                        <PermissionGate requiredPermissions={["UPDATE_COMMAND_SESSION"]}>
                                                            <DropdownMenuItem
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    updateSession({ sessionId: session.id, pinned: !session.pinned });
                                                                }}
                                                                disabled={isUpdatingSession}
                                                            >
                                                                {session.pinned ? (
                                                                    <>
                                                                        <PinOff className="mr-2 size-4" /> Unpin
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Pin className="mr-2 size-4" /> Pin
                                                                    </>
                                                                )}
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    startEdit(session);
                                                                }}
                                                            >
                                                                <Pencil className="mr-2 size-4" /> Rename
                                                            </DropdownMenuItem>
                                                        </PermissionGate>
                                                        <PermissionGate requiredPermissions={["DELETE_COMMAND_SESSION"]}>
                                                            <DropdownMenuItem
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setDeleteTargetId(session.id);
                                                                }}
                                                                disabled={isDeletingSession}
                                                                className="text-destructive focus:text-destructive"
                                                            >
                                                                <Trash2 className="mr-2 size-4" /> Delete
                                                            </DropdownMenuItem>
                                                        </PermissionGate>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
                {hasNextSessionsPage ? (
                    <div ref={sessionsLoadMoreRef} className="py-2">
                        {isFetchingNextSessionsPage ? <SessionListSkeleton count={2} /> : null}
                    </div>
                ) : null}

                <DeleteConfirmationDialog
                    open={deleteTargetId !== null}
                    onOpenChange={(open) => {
                        if (!open) setDeleteTargetId(null);
                    }}
                    onConfirm={handleDeleteConfirm}
                    isLoading={isDeletingSession}
                    itemType="chat"
                />
            </div>
        </div>
    );
}
